import { ApiError, apiClient } from "@/shared/apiClient";
import { logger } from "@/shared/logger";
import type {
  LeaveRequest,
  LeaveRequestPayload,
  LeaveRequestCancelResponse,
  LeaveRequestsListResult,
} from "./types";

const LEAVE_REQUESTS_BASE = "/api/employee/leave-requests";

export const leaveApi = {
  async createLeaveRequest(payload: LeaveRequestPayload): Promise<LeaveRequest> {
    logger.info("leave.create.request", { payload });
    try {
      const response = await apiClient.post(LEAVE_REQUESTS_BASE, payload);
      return response?.data ?? response;
    } catch (error) {
      if (error instanceof ApiError) {
        logger.error("leave.create.apiError", {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          responseBody: error.responseBody,
          payload,
        });
      } else {
        logger.error("leave.create.error", { error, payload });
      }
      throw error;
    }
  },

  async updateLeaveRequest(id: number, payload: LeaveRequestPayload): Promise<LeaveRequest> {
    logger.info("leave.update.request", { id, payload });
    try {
      const response = await apiClient.patch(`${LEAVE_REQUESTS_BASE}/${id}`, payload);
      return response?.data ?? response;
    } catch (error) {
      if (error instanceof ApiError) {
        logger.error("leave.update.apiError", {
          id,
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          responseBody: error.responseBody,
          payload,
        });
      } else {
        logger.error("leave.update.error", { id, error, payload });
      }
      throw error;
    }
  },

  async getLeaveRequests(): Promise<LeaveRequestsListResult> {
    const response = await apiClient.get(LEAVE_REQUESTS_BASE);
    if (Array.isArray(response)) {
      return {
        requests: response,
        count: response.length,
        days_remaining: 0,
        days_taken_last_18_months: 0,
        range: null,
      };
    }
    const requests = response?.data ?? [];
    return {
      requests,
      count: response?.count ?? requests.length,
      days_remaining: response?.days_remaining ?? 0,
      days_taken_last_18_months: response?.days_taken_last_18_months ?? 0,
      range:
        response?.range?.start_date && response?.range?.end_date
          ? {
              start_date: response.range.start_date,
              end_date: response.range.end_date,
            }
          : null,
    };
  },

  async getLeaveRequestById(id: number): Promise<LeaveRequest> {
    const response = await apiClient.get(`${LEAVE_REQUESTS_BASE}/${id}`);
    return response?.data ?? response;
  },

  async cancelLeaveRequest(id: number): Promise<LeaveRequestCancelResponse> {
    const response = await apiClient.patch(`${LEAVE_REQUESTS_BASE}/${id}/cancel`);
    return response?.data ?? response;
  },
};
